import React, { useState } from 'react';
import {
    Book,
    Shield,
    Zap,
    Database,
    Cloud,
    Lock,
    Smartphone,
    Layout,
    Image as ImageIcon,
    AlertCircle,
    Copy,
    Check,
    Search,
    BarChart3,
    Mail,
    ExternalLink
} from 'lucide-react';

type TabType = 'architecture' | 'vault' | 'operations';

const DocsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('architecture');
    const [copiedValue, setCopiedValue] = useState<string | null>(null);

    const handleCopy = (value: string) => {
        navigator.clipboard.writeText(value);
        setCopiedValue(value);
        setTimeout(() => setCopiedValue(null), 2000);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Navigation Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-4">
                <TabButton
                    active={activeTab === 'architecture'}
                    onClick={() => setActiveTab('architecture')}
                    icon={<Zap size={16} />}
                    label="Arquitectura"
                />
                <TabButton
                    active={activeTab === 'vault'}
                    onClick={() => setActiveTab('vault')}
                    icon={<Shield size={16} />}
                    label="Bóveda de Accesos"
                />
                <TabButton
                    active={activeTab === 'operations'}
                    onClick={() => setActiveTab('operations')}
                    icon={<Book size={16} />}
                    label="Manual de Operaciones"
                />
            </div>

            {/* Content Area */}
            <div className="mt-8">
                {activeTab === 'architecture' && <ArchitectureTab />}
                {activeTab === 'vault' && <VaultTab onCopy={handleCopy} copiedValue={copiedValue} />}
                {activeTab === 'operations' && <OperationsTab />}
            </div>
        </div>
    );
};

const TabButton: React.FC<{ active: boolean, onClick: () => void, icon: React.ReactNode, label: string }> = ({ active, onClick, icon, label }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-6 py-3 rounded-none text-xs font-bold uppercase tracking-widest transition-all ${active
            ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.2)]'
            : 'text-zinc-500 hover:text-white hover:bg-zinc-900'
            }`}
    >
        {icon} {label}
    </button>
);

const ArchitectureTab = () => (
    <div className="grid grid-cols-1 gap-8 max-w-2xl">
        <div className="space-y-6">
            <section className="bg-zinc-900/50 border border-zinc-800 p-8 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Database size={64} />
                </div>
                <h3 className="text-xl font-display font-bold text-white mb-4 italic uppercase">Stack Tecnológico Core</h3>
                <ul className="space-y-4">
                    <TechItem title="Frontend Framework" desc="React 18 + Vite" sub="Alta velocidad y rendimiento moderno." />
                    <TechItem title="Backend & Database" desc="Supabase (PostgreSQL)" sub="Auth, Realtime, Storage y SQL." />
                    <TechItem title="Infraestructura" desc="Cloudflare Pages & Workers" sub="Edge computing y CDN global." />
                    <TechItem title="Monitoreo" desc="Sentry" sub="Captura de errores en tiempo real." />
                </ul>
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Zap size={64} />
                </div>
                <h3 className="text-xl font-display font-bold text-white mb-4 italic uppercase">Flujo de Datos</h3>
                <div className="space-y-4 text-sm text-zinc-400">
                    <p><strong className="text-white">1. Entrega:</strong> Cloudflare sirve la app desde el nodo más cercano.</p>
                    <p><strong className="text-white">2. Capa de Datos:</strong> React se comunica con Supabase vía REST/Realtime.</p>
                    <p><strong className="text-white">3. Seguridad:</strong> RLS (Row Level Security) protege los datos a nivel de base de datos.</p>
                </div>
            </section>
        </div>
    </div>
);

const VaultTab: React.FC<{ onCopy: (v: string) => void, copiedValue: string | null }> = ({ onCopy, copiedValue }) => (
    <div className="max-w-4xl space-y-6">
        <div className="bg-red-500/10 border border-red-500/20 p-6 flex gap-4 items-center">
            <AlertCircle className="text-red-500 shrink-0" size={24} />
            <div>
                <h4 className="text-red-500 font-bold uppercase text-xs tracking-widest">Aviso de Seguridad Crítica</h4>
                <p className="text-zinc-400 text-xs mt-1">
                    Estos accesos controlan la infraestructura económica y técnica de Guzmán New Parts. No comparta estos datos por canales inseguros.
                </p>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <VaultCard
                title="Dominio y CDN"
                platform="Cloudflare"
                url="https://dash.cloudflare.com"
                user="guzmannewparts@gmail.com"
                icon={<Cloud size={20} />}
                onCopy={() => onCopy('https://dash.cloudflare.com')}
                isCopied={copiedValue === 'https://dash.cloudflare.com'}
            />
            <VaultCard
                title="SEO y Visibilidad"
                platform="Google Search Console"
                url="https://search.google.com/search-console"
                user="guzmannewparts@gmail.com"
                icon={<Search size={20} />}
                onCopy={() => onCopy('https://search.google.com/search-console')}
                isCopied={copiedValue === 'https://search.google.com/search-console'}
            />
            <VaultCard
                title="Métricas y Tráfico"
                platform="Google Analytics"
                url="https://analytics.google.com"
                user="guzmannewparts@gmail.com"
                icon={<BarChart3 size={20} />}
                onCopy={() => onCopy('https://analytics.google.com')}
                isCopied={copiedValue === 'https://analytics.google.com'}
            />
            <VaultCard
                title="Correo Corporativo"
                platform="Google Gmail"
                url="https://mail.google.com"
                user="guzmannewparts@gmail.com"
                icon={<Mail size={20} />}
                onCopy={() => onCopy('https://mail.google.com')}
                isCopied={copiedValue === 'https://mail.google.com'}
            />
        </div>
    </div>
);

const OperationsTab = () => (
    <div className="max-w-5xl space-y-12">
        <section className="space-y-6">
            <div className="flex items-center gap-4">
                <div className="h-px flex-grow bg-zinc-800" />
                <h2 className="text-sm font-mono font-bold text-zinc-500 uppercase tracking-[0.3em]">Gestión de Medios</h2>
                <div className="h-px flex-grow bg-zinc-800" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <OpCard
                    icon={<ImageIcon className="text-white" />}
                    title="Formato Recomendado"
                    desc="Use .PNG (transparente) o .WEBP. Evite .JPG pesados."
                />
                <OpCard
                    icon={<Layout className="text-white" />}
                    title="Proporción 4:5"
                    desc="Mantiene la simetría perfecta en móviles y escritorio."
                />
                <OpCard
                    icon={<Smartphone className="text-white" />}
                    title="Optimización"
                    desc="Máximo 500KB por imagen para carga instantánea."
                />
            </div>
        </section>

        <section className="bg-zinc-900 border border-zinc-800 p-8 rounded-sm">
            <h3 className="text-xl font-display font-bold text-white mb-6 italic uppercase flex items-center gap-3">
                <Zap className="text-white" size={24} />
                Configuración Narrativa (Framing)
            </h3>
            <div className="space-y-4 text-zinc-400 text-sm leading-relaxed">
                <p>El sistema de <span className="text-white font-bold">Framing</span> permite ajustar la "cámara" de cada producto en la portada:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                    <div className="bg-black/50 p-6 border border-zinc-800/50">
                        <h4 className="text-white font-bold mb-2 uppercase text-xs">Escala (Zoom)</h4>
                        <p>Valores recomendados entre 1.0 y 1.5. Permite forzar un primer plano para resaltar texturas del repuesto.</p>
                    </div>
                    <div className="bg-black/50 p-6 border border-zinc-800/50">
                        <h4 className="text-white font-bold mb-2 uppercase text-xs">Alineación X / Y</h4>
                        <p>Desplace el producto para centrarlo visualmente. La visualización en el Admin es idéntica a la pública.</p>
                    </div>
                </div>
                <div className="mt-8 p-4 bg-white/5 border-l-2 border-white text-[10px] uppercase tracking-widest">
                    Tip: Ajuste primero la escala y luego centre el objeto para mejores resultados.
                </div>
            </div>
        </section>
    </div>
);

const TechItem: React.FC<{ title: string, desc: string, sub: string }> = ({ title, desc, sub }) => (
    <li className="border-l border-zinc-800 pl-4 py-1">
        <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-tighter">{title}</p>
        <p className="text-white font-bold">{desc}</p>
        <p className="text-xs text-zinc-500">{sub}</p>
    </li>
);

const VaultCard: React.FC<{ title: string, platform: string, url: string, user: string, icon: React.ReactNode, onCopy: () => void, isCopied: boolean }> = ({ title, platform, url, user, icon, onCopy, isCopied }) => (
    <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-zinc-900 border border-zinc-800 p-5 group hover:border-white transition-all cursor-pointer relative overflow-hidden block decoration-none"
    >
        <div className="flex justify-between items-start mb-4">
            <div className="bg-white/5 p-2 rounded-none border border-zinc-800 group-hover:border-white/20 transition-colors">
                {icon}
            </div>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCopy();
                }}
                className="p-1 hover:bg-white/10 transition-colors rounded-sm"
            >
                {isCopied ? <Check size={14} className="text-green-500" /> : <Copy size={14} className="text-zinc-600 group-hover:text-white" />}
            </button>
        </div>
        <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">{title}</p>
        <div className="flex items-center gap-2 mt-1">
            <h4 className="text-lg font-bold text-white">{platform}</h4>
            <ExternalLink size={12} className="text-zinc-600 opacity-0 group-hover:opacity-100 transition-all" />
        </div>
        <p className="text-xs text-zinc-400 mt-2 font-mono">User ID: {user}</p>
    </a>
);

const OpCard: React.FC<{ icon: React.ReactNode, title: string, desc: string }> = ({ icon, title, desc }) => (
    <div className="bg-zinc-900/30 border border-zinc-800 p-6 hover:bg-zinc-900/50 transition-colors">
        <div className="mb-4">{icon}</div>
        <h4 className="text-white font-bold text-sm uppercase mb-2">{title}</h4>
        <p className="text-zinc-500 text-xs leading-relaxed">{desc}</p>
    </div>
);

export default DocsPage;
